import React, {useEffect, useMemo, useState} from 'react'
import {Bar} from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import {KTSVG} from '../../../_metronic/helpers/components/KTSVG'
import {useSelector} from 'react-redux'
import {useGetCustomerWiseShipmentQuery} from '../../redux/network/AdminDashboardApi'
import {useAuth} from '../../modules/auth'

// Register the components of Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const CustomerWiseShipmentBarChart = ({currency}: any) => {
  const currentUser = useAuth()
  const {startDate, endDate, customers, isFiltered} = useSelector(
    (state: any) => state.Filter.Filter
  )
  const {data: ShipmentData, error: shipmentError} = useGetCustomerWiseShipmentQuery(
    {startDate, endDate},
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: 0,
      skip: false,
    }
  )

  const customerList: Customer[] = currentUser?.currentUser?.user?.customers ?? []
  const selectedCustomerId = customers[0]

  type Customer = {
    customer_id: string
    customer_name: string
  }
  const selectedCustomer: Customer | undefined = customerList?.find(
    (customer: Customer) => customer.customer_id === selectedCustomerId
  )

  const initialLabelsOther = ShipmentData?.labels ?? []
  const initialData = ShipmentData?.data ?? []

  // State for dataset & sorting order
  const [labels, setLabels] = useState<string[]>(initialLabelsOther)
  const [dataSet, setDataSet] = useState<number[]>(initialData)
  const [isSortedAsc, setIsSortedAsc] = useState<boolean>(true)

  useEffect(() => {
    // Create combined array of labels and data
    const combined = (ShipmentData?.labels ?? []).map((label: string, index: number) => ({
      label,
      value: ShipmentData?.data?.[index] ?? 0,
    }))

    // Sort by value in ascending order by default
    const sortedData = [...combined].sort((a, b) => a.value - b.value)

    // Update state with sorted data
    setLabels(sortedData.map((item) => item.label))
    setDataSet(sortedData.map((item) => item.value))
  }, [ShipmentData])

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Number of Shipments',
          data: dataSet,
          backgroundColor: labels.map((label) =>
            label === selectedCustomer?.customer_name.trim()
              ? 'rgba(200, 33, 40, 0.9)'
              : 'rgba(200, 33, 40, 0.3)'
          ), // Red for even, Gray for odd
          borderRadius: 6,
        },
      ],
    }),
    [labels, dataSet, selectedCustomer?.customer_name]
  )

  const options = {
    indexAxis: 'x' as const,
    responsive: true,
    maintainAspectRatio: false,

    plugins: {
      legend: {
        display: false,
      },
      // title: {
      //   display: true,
      //   text: 'Customer Names',
      // },
      datalabels: {
        display: false, // Disable datalabels for this chart
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Customer Names',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        beginAtZero: true,
        ticks: {
          font: {
            size: 12,
          },
        },
        barPercentage: 2,
        categoryPercentage: 0.6,
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: 'Number of Shipments',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        ticks: {
          font: {
            size: 12,
            fontWeight: 'bold',
          },
        },
      },
    },
  }
  const handleSort = () => {
    // Create an array of objects with both labels and data
    const combined = labels.map((label, index) => ({
      label,
      value: dataSet[index],
    }))

    // Sort by value
    const sortedData = [...combined].sort((a, b) =>
      isSortedAsc ? b.value - a.value : a.value - b.value
    )

    // Extract the new sorted labels and values
    setLabels(sortedData.map((item) => item.label))
    setDataSet(sortedData.map((item) => item.value))

    // Toggle sorting order
    setIsSortedAsc(!isSortedAsc)
  }

  return (
    <div
      className='card mb-xl-8  card-xl-stretch '
      style={{
        border: '1px solid rgba(236, 111, 102, 0.1)',
      }}
    >
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold text-dark fs-2'>Customer-Wise Shipment </span>
          <span className='text-muted mt-1 fw-semibold fs-6'>Shipment Summary</span>
        </h3>
        <div className='card-toolbar'>
          <button
            className='btn btn-sm btn-icon btn-light-dark btn-active-light-dark btn-hover-primary'
            data-bs-toggle='tooltip'
            data-bs-placement='top'
            title='Sort'
            onClick={handleSort}
          >
            {isSortedAsc ? (
              <KTSVG
                path='media/icons/duotune/graphs/gra004.svg'
                className='svg-icon-danger svg-icon'
              />
            ) : (
              <KTSVG
                path='media/icons/duotune/graphs/gra005.svg'
                className='svg-icon-danger svg-icon'
              />
            )}
          </button>
        </div>
      </div>

      <div className='card-body pt-0 d-flex align-items-end mt-5' style={{height: '550px'}}>
        <Bar data={data} options={options} />
      </div>
    </div>
  )
}

export default CustomerWiseShipmentBarChart
